import { useQueryParams } from 'use-query-params'
import React, { Key, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Icon, Show } from '@app/components/util'
import { LntButton, LntDropdown } from '@app/components/base'
import { ContentLayout } from '@app/layouts'
import { PhotosUploadModal } from '@app/modals'
import { FileUploadModal } from 'src/modals/FileUploadModal'
import {
  ExtendedTable,
  Header,
  PermissionWrapper,
  Subheader,
  SubheaderToolbarDivider,
  TableSettingsToolbar
} from '@app/components'

import { objectHasEveryNullableProperties } from '@app/utils/object'
import { completeCorrectionTask, postUploadPrices } from '@app/api/routes/monitoringTasks/monitoringTasks'
import { getExportFilesDropdownMenu, uploadFileFactory } from '@app/utils'
import {
  exportAllMonitoringTasksFile,
  exportContractorsListFile,
  exportTaskValidationsTechCheckFile
} from '@app/utils/export/exportFile'
import { getTasks, completeTask, matchTask } from '@app/api/routes/monitoringTasks'
import {
  useToggle,
  useTableSearch,
  useExtendedNavigate,
  useNotification,
  usePaginationQueryParams,
  useTableSetting,
  useMultiStatusMutation
} from '@app/hooks'

import { FILE_TYPES, MONITORING_TASK_STATUS } from '@app/api/constants'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { MONITORINGS_TASKS_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'
import { CONTRACTOR_SECTION_PAGE } from '@app/constants/page'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { ICON_NAME } from '@app/constants/icon'

import { adaptContractorSectionTableData, adaptContractorSectionPageFilterData, getColumns } from './lib'
import { TopFilters } from './ui'
import { topFiltersQuerySchema, tableSearchQuerySchema, columnsSearchSchema } from './configs'

import type { FiltersParams } from '@app/types/filter'
import type { GetTasksParams } from '@app/api/routes/monitoringTasks/types'
import type { FileType } from '@app/types'
import type { ContractorSectionDataSource } from './types'

const columns = getColumns()

const title = 'Раздел подрядчика'

export const ContractorSectionPage: React.FC = () => {
  const queryClient = useQueryClient()
  const notification = useNotification()
  const navigate = useExtendedNavigate()

  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()
  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)

  const buttonLoadingExportAllToggle = useToggle()

  const monitoringsTasksParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetTasksParams

  const { data: contractorSectionTableData, isLoading: isMonitoringsTasksDataLoading } = useQuery({
    enabled: !objectHasEveryNullableProperties(queryFilters),
    queryKey: [MONITORINGS_TASKS_KEY.tasksItems, monitoringsTasksParams],
    queryFn: async () => await getTasks(monitoringsTasksParams),
    select: tasksData => adaptContractorSectionTableData(tasksData)
  })

  const completeTaskMutation = useMutation(completeTask, {
    onSettled: async () => await queryClient.invalidateQueries([MONITORINGS_TASKS_KEY.tasksItems])
  })

  const completeCorrectionTaskMutation = useMutation(completeCorrectionTask, {
    onSettled: async () => await queryClient.invalidateQueries([MONITORINGS_TASKS_KEY.tasksItems])
  })

  const matchTaskMutation = useMutation({
    mutationFn: matchTask,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItems] })

      notification.success({
        message: 'Cопоставление выполнено',
        description: 'Товары и фото сопоставлены, можно завершать мониторинг'
      })
    }
  })

  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [selectedRows, setSelectedRows] = useState<ContractorSectionDataSource[]>([])

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const uploadPricesModal = useToggle()
  const uploadPhotosModalToggle = useToggle()

  const handleTaskValidationsTechCheckFile = async (): Promise<void> => {
    if (!selectedIds.length) return

    const fileName = 'Список_технических_ошибок'

    await exportTaskValidationsTechCheckFile(fileName, { id: selectedIds[0], file: FILE_TYPES.xlsx })

    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItems] })
  }

  const { mutation: postUploadPricesMutation, modalContextHolder } = useMultiStatusMutation({
    mutationFn: postUploadPrices,
    onSuccess: async () => {
      notification.success({
        message: 'Успешно:',
        description: 'Цены успешно загружены!'
      })
    },
    footer: (
      <LntButton
        type={ BUTTON_TYPE.secondary }
        size={ SIZE.middle }
        onClick={ handleTaskValidationsTechCheckFile }
      >
        Экспорт ошибок
      </LntButton>
    )
  })

  const handlePhotosUploadFinish = async (): Promise<void> => {
    await Promise.allSettled([
      queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] }),
      queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItemPhotos] })
    ])
  }

  const handleDownloadPrices = uploadFileFactory({
    id: selectedIds[0],
    callback: async (id: number, file: File): Promise<void> => {
      const pricesFormData = new FormData()

      pricesFormData.append('file', file, file.name)

      const data = pricesFormData

      try {
        await postUploadPricesMutation.mutateAsync({ id, data })
      } finally {
        await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksResults] })
        uploadPricesModal.setUntoggled()
      }
    }
  })

  const handleSelectedRowChange = (selectedKeys: Key[], selectedRows: ContractorSectionDataSource[]): void => {
    setSelectedIds(selectedKeys.map(Number))
    setSelectedRows(selectedRows)
  }

  const handleExportById = async (fileType: FileType): Promise<void> => {
    if (!selectedIds.length) return

    const fileName = 'Список_на_мониторинг'

    await exportContractorsListFile(fileName, { ids: selectedIds, file: fileType })

    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItems] })
  }

  const handleMatchProductAndPhoto = async (): Promise<void> => {
    const id = selectedIds[0]

    if (id === undefined) return

    matchTaskMutation.mutate({ id })
  }

  const handleNavigateToInnerPage = (_: unknown, record: ContractorSectionDataSource): void => {
    navigate(`${CONTRACTOR_SECTION_PAGE.contractorSection}/${record.key}`)
  }

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handleCompleteTask = async (): Promise<void> => {
    await completeTaskMutation.mutateAsync({ id: selectedIds[0] })
  }

  const handleCompleteCorrectionTask = async (): Promise<void> => {
    await completeCorrectionTaskMutation.mutateAsync({ id: selectedIds[0] })
  }

  const handleNavigateToInnerPageBySelected = (): void => {
    const id = selectedIds[0]

    navigate(`${CONTRACTOR_SECTION_PAGE.contractorSection}/${id}`)
  }

  const handleExportAllMonitoringTasks = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportAllToggle.setToggled()

    const contractorSectionPageFilterData = adaptContractorSectionPageFilterData(queryFilters)

    const data = {
      ...contractorSectionPageFilterData,
      fileFormat: fileType
    }

    const fileName = 'Задачи на мониторинг'

    try {
      await exportAllMonitoringTasksFile(fileName, data)
    } finally {
      buttonLoadingExportAllToggle.setUntoggled()
    }
  }

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Список на мониторинг',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportById
  )
  const exportDropdownAllItems = getExportFilesDropdownMenu(
    'Список на мониторинг',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportAllMonitoringTasks
  )

  const subheaderToolbar = (
    <PermissionWrapper requiredPermissions={ [PERMISSION.uploadMonitoringProductOrCompetitorLists] }>
      <LntDropdown
        trigger={ ['click'] }
        items={ exportDropdownAllItems }
      >
        <LntButton
          type={ BUTTON_TYPE.primary }
          size={ SIZE.small }
          loading={ buttonLoadingExportAllToggle.value }
        >
          Экспорт списков
          <Icon name={ ICON_NAME.arrowDown } />
        </LntButton>
      </LntDropdown>
    </PermissionWrapper>
  )

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.contractorSectionPage, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredMonitoringsTasksDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: contractorSectionTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar = [
    <Show
      key='send-notification-show'
      condition={ selectedIds.length === 1 }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleNavigateToInnerPageBySelected }
      >
        Просмотр
      </LntButton>

      <SubheaderToolbarDivider />
    </Show>,

    <Show
      key='restart_stop_end_сompare-products-and-photos-show'
      condition={ selectedIds.length === 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.downloadMonitoringResults] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ uploadPricesModal.setToggled }
        >
          Загрузить цены
        </LntButton>
      </PermissionWrapper>
      <PermissionWrapper requiredPermissions={ [PERMISSION.downloadMonitoringResults] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ uploadPhotosModalToggle.setToggled }
        >
          Загрузить фото
        </LntButton>
      </PermissionWrapper>
      <PermissionWrapper requiredPermissions={ [PERMISSION.downloadMonitoringResults] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleMatchProductAndPhoto }
        >
          Сопоставить товары и фото
        </LntButton>
      </PermissionWrapper>
      <Show condition={ selectedRows[0]?.status === MONITORING_TASK_STATUS.IN_PROGRESS }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleCompleteTask }
        >
          Завершить мониторинг
        </LntButton>
      </Show>
      <Show condition={ selectedRows[0]?.status === MONITORING_TASK_STATUS.NEEDS_CORRECTION }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleCompleteCorrectionTask }
        >
          Завершить мониторинг
        </LntButton>
      </Show>
      <SubheaderToolbarDivider />
    </Show>,

    <Show
      key='export-list-show'
      condition={ selectedIds.length >= 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.uploadMonitoringProductOrCompetitorLists] }>
        <LntDropdown
          trigger={ ['click'] }
          items={ exportDropdownItems }
        >
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
          >
            Экспорт
            <Icon name={ ICON_NAME.arrowDown } />
          </LntButton>
        </LntDropdown>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <>
      <Header controlProps={ { title } } />

      <Subheader toolbar={ subheaderToolbar } />

      <ContentLayout>
        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ queryFilters as FiltersParams }
        />

        <ExtendedTable
          loading={ isMonitoringsTasksDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredMonitoringsTasksDataSource }
          toolbar={ toolbar }
          rowSelection={ {
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: contractorSectionTableData?.count,
            onChange: handlePaginationChange
          } }
          onRowDoubleClick={ handleNavigateToInnerPage }
        />
      </ContentLayout>

      <FileUploadModal
        title='Добавление цен конкурентов'
        open={ uploadPricesModal.value }
        onCancel={ uploadPricesModal.setUntoggled }
        onFileUpload={ handleDownloadPrices }
      />

      <PhotosUploadModal
        taskId={ selectedIds[0] }
        open={ uploadPhotosModalToggle.value }
        onCancel={ uploadPhotosModalToggle.setUntoggled }
        title='Добавление фото'
        onPhotosUploadFinish={ handlePhotosUploadFinish }
      />

      { modalContextHolder }
    </>
  )
}
